// Pre-launch gate, rendered instead of the app when VITE_COMING_SOON === 'true'
// (set in .env.prod). Flip to false in .env.prod to go live. Shared structure;
// only the CONTENT block is per-product.
import { useState } from 'react';

const CONTENT = {
  name: 'Educza',
  kicker: 'School Management — Launching Soon',
  headlineLead: 'Run your school,',
  headlineAccent: 'all in one place.',
  headlineTail: '',
  pitch: 'Educza brings admissions, fees, attendance, exams, and communication into one platform for schools. We’re putting on the finishing touches.',
  highlights: [
    { title: 'Students & Staff', desc: 'Profiles, classes, and timetables.' },
    { title: 'Fees & Finance', desc: 'Online fee collection and reports.' },
    { title: 'Attendance & Exams', desc: 'Marks, report cards, and analytics.' },
  ],
  contactEmail: 'hello@educza.com',
  primary: '#2563EB',
  accent: '#16A34A',
};

export default function ComingSoon() {
  const c = CONTENT;
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (email.trim()) setSent(true);
  };

  return (
    <div style={S.wrap}>
      <div style={{ ...S.orb, top: -160, left: -160, background: glow(c.primary, 0.35) }} />
      <div style={{ ...S.orb, bottom: -200, right: -120, width: 620, height: 620, background: glow(c.accent, 0.28) }} />

      <div style={S.content}>
        <span style={{ ...S.kicker, background: alpha(c.primary, 0.14), border: `1px solid ${alpha(c.primary, 0.4)}`, color: tint(c.primary) }}>
          {c.kicker}
        </span>

        <h1 style={S.headline}>
          {c.headlineLead}{' '}
          <span style={{ ...S.gradient, backgroundImage: `linear-gradient(90deg, ${c.primary}, ${c.accent})` }}>
            {c.headlineAccent}
          </span>
          {c.headlineTail ? <><br />{c.headlineTail}</> : null}
        </h1>

        <p style={S.pitch}>{c.pitch}</p>

        <div style={S.highlights}>
          {c.highlights.map((h) => (
            <div key={h.title} style={{ ...S.card, borderTop: `2px solid ${alpha(c.primary, 0.6)}` }}>
              <div style={S.cardTitle}>{h.title}</div>
              <div style={S.cardDesc}>{h.desc}</div>
            </div>
          ))}
        </div>

        {sent ? (
          <div style={{ ...S.thanks, color: tint(c.primary) }}>You’re on the list — we’ll email you at launch. ✦</div>
        ) : (
          <form onSubmit={submit} style={S.form}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              style={S.input}
            />
            <button type="submit" style={{ ...S.btn, background: `linear-gradient(135deg, ${c.primary}, ${c.accent})` }}>
              Get early access
            </button>
          </form>
        )}
        <div style={S.formNote}>Get early access, exclusive deals, and launch offers straight to your inbox.</div>

        <div style={S.footer}>
          Questions?{' '}
          <a href={`mailto:${c.contactEmail}`} style={{ ...S.link, color: tint(c.primary) }}>
            {c.contactEmail}
          </a>
        </div>
      </div>

      <div style={S.copyright}>
        © {new Date().getFullYear()} {c.name} · A Stacza product
      </div>
    </div>
  );
}

// ── helpers ──
const alpha = (hex, a) => {
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};
const glow = (hex, a) => `radial-gradient(circle, ${alpha(hex, a)} 0%, ${alpha(hex, 0)} 70%)`;
const tint = (hex) => {
  const n = hex.replace('#', '');
  const mix = (i) => Math.round(parseInt(n.slice(i, i + 2), 16) + (255 - parseInt(n.slice(i, i + 2), 16)) * 0.55);
  return `rgb(${mix(0)},${mix(2)},${mix(4)})`;
};

const S = {
  wrap: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #16121f 0%, #0a0a12 60%, #050509 100%)',
    color: '#f4f2fb',
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '56px 24px',
    textAlign: 'center',
  },
  orb: { position: 'absolute', width: 520, height: 520, borderRadius: '50%', filter: 'blur(45px)', pointerEvents: 'none' },
  content: { position: 'relative', zIndex: 1, maxWidth: 820, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  kicker: { display: 'inline-block', padding: '6px 14px', borderRadius: 999, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 24 },
  headline: { fontSize: 'clamp(34px, 6vw, 62px)', lineHeight: 1.06, fontWeight: 800, margin: '0 0 20px', letterSpacing: '-0.02em' },
  gradient: { WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' },
  pitch: { fontSize: 17, lineHeight: 1.6, color: 'rgba(244,242,251,0.7)', maxWidth: 600, margin: '0 0 36px' },
  highlights: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, width: '100%', maxWidth: 720, marginBottom: 40 },
  card: { background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '18px 18px', textAlign: 'left' },
  cardTitle: { fontSize: 15, fontWeight: 700, marginBottom: 6 },
  cardDesc: { fontSize: 13, lineHeight: 1.5, color: 'rgba(244,242,251,0.55)' },
  form: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', width: '100%', maxWidth: 460, marginBottom: 10 },
  input: { flex: '1 1 220px', padding: '13px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 14, outline: 'none' },
  btn: { padding: '13px 22px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  thanks: { fontSize: 15, fontWeight: 600, marginBottom: 10 },
  formNote: { fontSize: 12.5, color: 'rgba(244,242,251,0.4)', marginBottom: 34 },
  footer: { fontSize: 14, color: 'rgba(244,242,251,0.55)' },
  link: { textDecoration: 'none', borderBottom: '1px solid currentColor' },
  copyright: { position: 'absolute', bottom: 22, fontSize: 12, color: 'rgba(244,242,251,0.32)', zIndex: 1 },
};
